import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ProjectPieChart } from './ProjectPieChart';
import { EmployeePieChart } from './EmployeePieChart';
import { formatDuration } from '@/lib/format-duration';
import { localDateString } from '@/lib/time-entry-time';
import { fetchTimeStats, type TimeStats } from '../api';

interface TimeTrackingStatsProps {
  onRefresh?: () => void;
}

/** The current calendar month, in the user's local time (YYYY-MM-DD both ends). */
function currentMonthRange(now = new Date()) {
  return {
    from: localDateString(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: localDateString(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
}

/**
 * Statistics: every figure comes from GET /api/time-entries/stats in whole
 * seconds (computed by the server from start/end) and is shown as hours +
 * minutes by the one shared formatter -- never as decimal hours.
 */
export function TimeTrackingStats(_props: TimeTrackingStatsProps) {
  const [stats, setStats] = useState<TimeStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      setStats(await fetchTimeStats(currentMonthRange()));
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[20rem]">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="text-error mb-4">Error: {error || 'No data'}</div>
          <button 
            onClick={fetchStats}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="subheading-text text-sm">Total Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h1 text-2xl">{formatDuration(stats.total_seconds)}</div>
            <p className="text-muted text-xs">
              This month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="subheading-text text-sm">Avg Session</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h1 text-2xl">{formatDuration(stats.average_seconds)}</div>
            <p className="text-muted text-xs">
              Per time entry
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="subheading-text text-sm">Most Active Project</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h1 text-2xl">{stats.most_active_project?.name ?? 'N/A'}</div>
            <p className="text-muted text-xs">
              This month ({formatDuration(stats.most_active_project?.seconds ?? 0)})
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="subheading-text text-sm">Total Active Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h1 text-2xl">{stats.active_projects}</div>
            <p className="text-muted text-xs">
              Active this month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Projects Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="subheading-text text-lg">Project Time Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.project_breakdown.length > 0 ? (
              <ProjectPieChart data={stats.project_breakdown} />
            ) : (
              <div className="text-center py-8 text-muted">No project data available</div>
            )}
          </CardContent>
        </Card>

        {/* Employees Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="subheading-text text-lg">Employee Time Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.employee_breakdown.length > 0 ? (
              <EmployeePieChart data={stats.employee_breakdown} />
            ) : (
              <div className="text-center py-8 text-muted">No employee data available</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
